// Stand-ins (real people are never named): Sam is fictional; see CLAUDE.md "Real people are never named".
// Sugalarm — the Settings → Voice "Speak sample" utterance (cycle 330.2).
//
// Previews a REAL announcement using the active member's own name, glucose,
// and trend:
//
//   "<Display name>. Your glucose is <value> <units> and trending <word>."
//
// It replaces the old hardcoded `"Glucose 120, steady, in range."`, which
// both invented a glucose value AND asserted a zone ("in range") that had
// nothing to do with the member's actual state.
//
// TWO RULES THIS MODULE EXISTS TO HONOR — read before editing:
//
//  1. IT MAKES NO ZONE CLAIM. The cycle-309 contract is that no client code
//     may map a glucose value to a SPOKEN zone — the spoken zone comes from
//     the server's `classification`, never from a local threshold compare.
//     This sentence speaks only the raw value + the trend, so it needs no
//     classification and cannot contradict one. That is also why it needs no
//     veracity gate. Do NOT add "in range" / "low" / "high" here.
//
//  2. IT NEVER ASSERTS A VALUE IT CAN'T STAND BEHIND. With no reading, or a
//     STALE one, it says so rather than reading out a number that isn't true.
//     A voice sample that lies about glucose is worse than one that admits it
//     doesn't know.
//
// Trend wording routes through the canonical `contractTrendWord` (shared with
// the real announcement pipeline) so the sample can't drift from what an
// actual announcement says; a trendless reading omits the clause entirely
// rather than inventing "steady".
//
// This is a MANUAL, explicitly-requested utterance: quiet hours never gate
// it. It speaks at any hour.

import { displayValue, isStale } from "./glucose";
import type { Reading } from "./types";
import { contractTrendWord, spokenUnits } from "./utterance";

export function voiceSampleText(
  memberName: string | null | undefined,
  reading: Reading | null,
  units: "mg/dL" | "mmol/L",
  now: number = Date.now(),
): string {
  // Cycle 330.3 — the name is joined with a COMMA, not a full stop.
  // "Sam. Your glucose is…" made the engine land the name hard, as its own
  // clipped sentence, and the operator reported it read "like an emergency"
  // and startled them. "the member, your glucose is…" keeps it as one flowing,
  // ambient line. (The calm delivery itself — slower speed, no style
  // exaggeration, no speaker boost — is set server-side in tts_service.)
  const who = memberName?.trim() ? `${memberName.trim()}, ` : "";
  // Only the leading word of the sentence is capitalized; with a name in
  // front, "your" stays lowercase so it reads as one continuous sentence.
  const your = who ? "your" : "Your";

  if (!reading || isStale(reading.timestamp, now, reading.provider)) {
    return `${who}${your} glucose reading isn't available right now.`;
  }

  // Units are SPELLED OUT — a TTS engine reads "mg/dL" as garbage. Shared
  // mapping (`spokenUnits`), never inlined here.
  const value = `${displayValue(reading.sgv, units)} ${spokenUnits(units)}`;
  const trend = contractTrendWord(reading.trend, reading.trend_rate ?? null);

  return trend
    ? `${who}${your} glucose is ${value}, and trending ${trend}.`
    : `${who}${your} glucose is ${value}.`;
}
